import { useRequiredContext } from "@redotech/react-util/context";
import { useWindowEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { HiddenInput, HiddenInputHandle } from "@redotech/react-util/input";
import { LoadState } from "@redotech/react-util/load";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  FulfillmentGroupData,
  shouldUseVerifiedDestinationAddress,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  FulfillmentGroupStatus,
  isDoneStatus,
  isEditingLockedStatus,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { primaryCarrierShipment } from "@redotech/redo-fulfillment-group-common/shipment-summary";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { OutboundParcel } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { Preset } from "@redotech/redo-fulfillment-parcel-common/presets";
import TriangleRightIcon from "@redotech/redo-icons/play_right_filled.svg";
import TriangleDownIcon from "@redotech/redo-icons/triangle-down_filled.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { LocationDropdown } from "@redotech/redo-web/locations/location-dropdown";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useRef } from "react";
import { InsuranceSection } from "../../common/insurance-section";
import { OrderFulfillmentAddress } from "../../common/order-fulfillment-address";
import { PresetSelection } from "../../common/preset-selection";
import {
  ShipmentRateSelectionDropdown,
  SimpleShipmentRateSelectionDropdown,
} from "../../common/shipment-rate-selection-dropdown";
import { OutboundRateOption } from "../../common/util";
import { useSlideoutConfig } from "../../hooks/use-slideout-config";
import { getEffectiveShipmentSummary } from "../../utils/effective-shipment-summary";
import {
  areLocationsMixed,
  getLocationsForMove,
  getSelectedLocationId,
} from "../../utils/get-groups-locations";
import { groupDataPickupInRedoButNotInShopify } from "../../utils/pickup-override";
import {
  useContainsTiktokShippingOrder,
  useFulfillmentGroupAdjustments,
  useFulfillmentGroupUsedLearning,
} from "../../utils/use-group-info";
import { SCAN_CHAR } from "../../wms/shared/use-barcode-scanner";
import { CheckoutRateSection } from "./checkout-rate-section";
import { ShipmentAdjustmentsSection } from "./shipment-adjustments-section";

export const FulfillmentShippingInfoCard = memo(
  function FulfillmentShippingInfoCard({
    availableRates,
    displayedButtonAction,
    fulfillmentGroups,
    mixedRates,
    onEditAddress,
    onChangeUseAutoCleanedAddress,
    onSelectedRateChange,
    onPresetSelect,
    presets,
    selectedPreset,
    requestedShippingService,
    requestedShippingServiceDescription,
    selectedRate,
    selectedStatus,
    ratesPending,
    onSelectedLocationChange,
    everythingDisabled,
    parcelBeingEditedPerGroup,
    parcelBeingEditedIndex,
    updateParcel,
    updateRows,
  }: {
    availableRates: readonly OutboundRateOption[];
    displayedButtonAction: FulfillmentGroupActions;
    fulfillmentGroups: readonly FulfillmentGroupData[];
    mixedRates: boolean;
    onPresetSelect: (preset: Preset) => void;
    presets: LoadState<readonly Preset[]>;
    selectedPreset: Preset | undefined;
    onEditAddress: (address: FulfillmentOrderAddress) => Promise<void>;
    onChangeUseAutoCleanedAddress: (useAutoCleanedAddress: boolean) => void;
    onSelectedRateChange: (
      rate: OutboundRateOption | RedoShopifyDeliveryMethodType,
    ) => void;
    requestedShippingService: string;
    requestedShippingServiceDescription: string | undefined;
    selectedRate: OutboundRateOption | null;
    selectedStatus: FulfillmentGroupStatus | null | undefined;
    ratesPending: boolean;
    onSelectedLocationChange: (locationId: string) => void;
    everythingDisabled: boolean;
    parcelBeingEditedPerGroup?: OutboundParcel[];
    parcelBeingEditedIndex?: number;
    updateParcel?: (parcel: Partial<OutboundParcel>) => void;
    updateRows?: (updates: FulfillmentGroupUpdate[]) => void;
  }) {
    const selectPreset = (value: string) => {
      if (isEditingLockedStatus(selectedStatus)) {
        return;
      }
      for (const preset of presets.value ?? []) {
        if (value.toUpperCase() === preset.hotkey.replace("⌘", "")) {
          onPresetSelect(preset);
          return true;
        }
      }
      return false;
    };

    useWindowEventListener(window, "keydown", (e) => {
      if (e.key === SCAN_CHAR && e.target !== hiddenInputRef.current) {
        hiddenInputRef.current?.focus();
      } else if (e.ctrlKey || e.metaKey) {
        const presetFound = selectPreset(e.key);
        if (presetFound) {
          e.preventDefault();
        }
      }
    });

    const scanSubmitHandler = useHandler((value: string) => {
      selectPreset(value.replace(SCAN_CHAR, ""));
    });

    const team = useRequiredContext(TeamContext);
    const { addressExpanded, setAddressExpanded } = useSlideoutConfig();

    const hasOnlyShipping = fulfillmentGroups.every(
      (group) =>
        group.deliveryMethod === RedoShopifyDeliveryMethodType.Shipping,
    );

    const totalShippingPaid = useMemo(() => {
      if (
        !fulfillmentGroups.every((group) =>
          Number.isFinite(Number(group.totalShippingCost.amount)),
        )
      ) {
        return undefined;
      }

      // Everything in OMS right now is assuming USD, so I do the same here.
      // See shipping-paid-column.tsx.
      return fulfillmentGroups.reduce((acc, group) => {
        return acc + Number(group.totalShippingCost.amount);
      }, 0);
    }, [fulfillmentGroups]);

    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const deliveryMethod = fulfillmentGroups[0].deliveryMethod;

    const hiddenInputRef = useRef<HiddenInputHandle>(null);

    const bulkUpdate = fulfillmentGroups.length > 1;

    const selectedLocationId = getSelectedLocationId(fulfillmentGroups);
    const isMixed = areLocationsMixed(fulfillmentGroups);
    const locations = getLocationsForMove(fulfillmentGroups);
    const statusPending = fulfillmentGroups.some(
      (group) => group.status === FulfillmentGroupStatus.Pending,
    );

    const { usedShippingLearning } =
      useFulfillmentGroupUsedLearning(fulfillmentGroups);

    const containsTiktokShippingOrder =
      useContainsTiktokShippingOrder(fulfillmentGroups);

    const adjustments = useFulfillmentGroupAdjustments(fulfillmentGroups);
    const hasAdjustments = adjustments.length > 0;

    const isPickupInRedoButDeliveryInShopify = useMemo(() => {
      return fulfillmentGroups.some(groupDataPickupInRedoButNotInShopify);
    }, [fulfillmentGroups]);

    const showDumbShipmentRate = useMemo(() => {
      const someClosedWithoutBoughtRate = fulfillmentGroups.some(
        (group) =>
          isDoneStatus(group.status) &&
          !primaryCarrierShipment(getEffectiveShipmentSummary(group))
            ?.purchased,
      );
      return someClosedWithoutBoughtRate;
    }, [fulfillmentGroups]);

    const displayAddress =
      fulfillmentGroups[0]?.verifiedDestinationAddress &&
      shouldUseVerifiedDestinationAddress(
        fulfillmentGroups[0],
        team.settings?.outboundLabels?.address?.autoCleanAddresses,
      )
        ? fulfillmentGroups[0]?.verifiedDestinationAddress
        : fulfillmentGroups[0]?.destinationAddress;

    return (
      <Flex dir="column" gap="2xl">
        {hasOnlyShipping && !bulkUpdate && (
          <Flex dir="column" gap="xs">
            <Flex
              align="center"
              justify="space-between"
              onClick={() => setAddressExpanded(!addressExpanded)}
              style={{ cursor: "pointer" }}
            >
              <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
                Shipping address
              </Text>
              <RedoButton
                IconLeading={
                  addressExpanded ? TriangleDownIcon : TriangleRightIcon
                }
                size="xs"
              />
            </Flex>
            {addressExpanded && displayAddress && (
              <OrderFulfillmentAddress
                displayAddress={displayAddress}
                fontWeight="medium"
                hideEditButton={
                  everythingDisabled ||
                  isEditingLockedStatus(selectedStatus) ||
                  containsTiktokShippingOrder
                }
                onChangeUseAutoCleanedAddress={onChangeUseAutoCleanedAddress}
                // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                originalAddress={fulfillmentGroups[0].destinationAddress}
                titleText={null}
                updateAddress={onEditAddress}
              />
            )}
          </Flex>
        )}

        <Flex>
          <LocationDropdown
            disabled={
              everythingDisabled || isEditingLockedStatus(selectedStatus)
            }
            hideLoadingIndicator
            loading={statusPending || ratesPending}
            locations={locations}
            mixed={isMixed}
            onSelectedLocationChange={onSelectedLocationChange}
            selectedLocationId={selectedLocationId}
          />
        </Flex>

        {hasOnlyShipping && presets.value && presets.value.length > 0 && (
          <Flex>
            <HiddenInput onSubmit={scanSubmitHandler} ref={hiddenInputRef} />
            <PresetSelection
              disabled={
                everythingDisabled || isEditingLockedStatus(selectedStatus)
              }
              onPresetSelect={onPresetSelect}
              presets={presets.value}
              selectedPreset={selectedPreset}
            />
          </Flex>
        )}
        {showDumbShipmentRate ? (
          <SimpleShipmentRateSelectionDropdown groups={fulfillmentGroups} />
        ) : (
          <Flex flexDirection="column" gap="sm">
            <ShipmentRateSelectionDropdown
              deliveryMethod={deliveryMethod}
              disabled={
                everythingDisabled ||
                ratesPending ||
                isEditingLockedStatus(selectedStatus) ||
                displayedButtonAction ===
                  FulfillmentGroupActions.MARK_AS_FULFILLED ||
                isPickupInRedoButDeliveryInShopify
              }
              fulfillmentGroups={fulfillmentGroups}
              fullWidth
              includeAlternateDeliveryMethods
              label="Service"
              loading={ratesPending}
              mixedRates={mixedRates}
              options={availableRates}
              selectedRate={selectedRate}
              setSelectedRate={onSelectedRateChange}
              totalShippingPaid={totalShippingPaid}
              usedShippingLearning={usedShippingLearning}
            />
            {fulfillmentGroups[0]?.ratesErrorMessage &&
              fulfillmentGroups.length === 1 && (
                <Text fontSize="xs" fontWeight="semibold" textColor="error">
                  {fulfillmentGroups[0].ratesErrorMessage}
                </Text>
              )}
          </Flex>
        )}
        {hasAdjustments && (
          <ShipmentAdjustmentsSection
            adjustments={adjustments}
            fulfillmentGroupId={fulfillmentGroups[0]!._id} // eslint-disable-line @typescript-eslint/no-non-null-assertion
          />
        )}
        {hasOnlyShipping && (
          <CheckoutRateSection
            fulfillmentGroups={fulfillmentGroups}
            requestedShippingService={requestedShippingService}
            requestedShippingServiceDescription={
              requestedShippingServiceDescription
            }
            selectedRate={selectedRate}
          />
        )}
        {hasOnlyShipping &&
          parcelBeingEditedPerGroup &&
          updateParcel &&
          updateRows && (
            <InsuranceSection
              everythingDisabled={everythingDisabled}
              fulfillmentGroups={fulfillmentGroups}
              isEditingLockedStatus={isEditingLockedStatus(selectedStatus)}
              parcelBeingEditedIndex={parcelBeingEditedIndex}
              parcelBeingEditedPerGroup={parcelBeingEditedPerGroup}
              updateParcel={updateParcel}
              updateRows={updateRows}
            />
          )}
      </Flex>
    );
  },
);
